import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme }        from "@mui/material/styles";
import MicrosoftLoginButton from "../MicrosoftLoginButton";

const RoleCard = ({ card, onClick }) => {
  const theme        = useTheme();
  const t            = theme.custom;
  const accentColor  = t[card.accentKey];
  const accent2Color = t[card.accent2Key];
  const isDark       = theme.palette.mode === "dark";
  const isComingSoon = !!card.comingSoon;

  const [shaking, setShaking] = useState(false);

  const handleComingSoonClick = () => {
    if (shaking) return;
    setShaking(true);
    setTimeout(() => setShaking(false), 500);
  };

  if (!isComingSoon) {
    return (
      <Box
        sx={{
          position:     "relative",
          background:   t.surface2,
          border:       `1px solid ${t.border2}`,
          borderRadius: "4px",
          padding:      "28px 26px 24px",
          overflow:     "hidden",
          transition:   "border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease",
          "&::before": {
            content:    '""',
            position:   "absolute",
            top:        0, left: 0, right: 0,
            height:     "3px",
            background: accentColor,
            transition: "transform 0.3s ease",
          },
          "&::after": {
            content:       '""',
            position:      "absolute",
            inset:         0,
            background:    `radial-gradient(ellipse at top, ${accent2Color} 0%, transparent 70%)`,
            opacity:       0,
            transition:    "opacity 0.4s ease",
            pointerEvents: "none",
          },
          "&:hover": {
            borderColor: t.border3,
            transform:   "translateY(-6px)",
            boxShadow:   isDark
              ? "0 16px 48px rgba(0,0,0,0.5)"
              : "0 8px 32px rgba(3,81,77,0.12)",
            "&::after": { opacity: 1 },
          },
          "&:active": {
            transform:  "translateY(-2px)",
            transition: "transform 0.1s ease",
          },
        }}
      >
        <Box
          sx={{
            display:       "inline-block",
            fontFamily:    "'Inter', 'Courier New', monospace",
            fontSize:      "9px",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            padding:       "3px 8px",
            borderRadius:  "2px",
            background:    accent2Color,
            color:         accentColor,
            mb:            "14px",
          }}
        >
          {card.badge}
        </Box>

        <Typography
          sx={{
            fontFamily:    "'Inter', sans-serif",
            fontSize:      "20px",
            fontWeight:    700,
            letterSpacing: "-0.01em",
            color:         t.text,
            mb:            "4px",
            lineHeight:    1.2,
            transition:    "color 0.3s ease",
            ".MuiBox-root:hover &": { color: accentColor },
          }}
        >
          {card.title}
        </Typography>

        <Typography
          sx={{
            fontSize:   "11.5px",
            color:      t.text3,
            fontWeight: 300,
            lineHeight: 1.65,
            mb:         "24px",
            transition: "color 0.3s ease",
            ".MuiBox-root:hover &": { color: t.text2 },
          }}
        >
          {card.desc}
        </Typography>

        <Box sx={{ transition: "transform 0.3s ease", ".MuiBox-root:hover &": { transform: "translateY(-2px)" } }}>
          <MicrosoftLoginButton onClick={() => onClick(card.id)} />
        </Box>
      </Box>
    );
  }

  // ── Coming Soon card ──────────────────────────────────────────────────────
  return (
    <Box
      onClick={handleComingSoonClick}
      sx={{
        position:     "relative",
        background:   t.surface2,
        border:       `1px solid ${t.border2}`,
        borderRadius: "4px",
        padding:      "28px 26px 24px",
        overflow:     "hidden",
        cursor:       "pointer",
        userSelect:   "none",
        transition:   "border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease",
        animation:    shaking ? "cs-shake 0.45s ease" : "none",
        "@keyframes cs-shake": {
          "0%,100%": { transform: "translateX(0)" },
          "20%":     { transform: "translateX(-5px)" },
          "40%":     { transform: "translateX(5px)" },
          "60%":     { transform: "translateX(-4px)" },
          "80%":     { transform: "translateX(4px)" },
        },
        // Top accent bar — muted
        "&::before": {
          content:    '""',
          position:   "absolute",
          top:        0, left: 0, right: 0,
          height:     "3px",
          background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
          transition: "background 0.25s ease",
        },
        // Hover: lift + border sharpens slightly
        "&:hover": {
          borderColor: t.border3,
          transform:   "translateY(-4px)",
          boxShadow:   isDark
            ? "0 12px 36px rgba(0,0,0,0.45)"
            : "0 6px 24px rgba(0,0,0,0.08)",
          // overlay intensifies on hover (handled via group class below)
          "& .cs-overlay": {
            backdropFilter: "blur(3px)",
            background:     isDark
              ? "rgba(8,13,13,0.72)"
              : "rgba(240,245,245,0.72)",
          },
          "& .cs-pill": {
            borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.18)",
            background:  isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
          },
          "& .cs-lock": {
            opacity: 0.55,
            transform: "scale(1.1)",
          },
          "& .cs-label": {
            color: t.text2,
          },
        },
        // Click: push down
        "&:active": {
          transform:  "translateY(-1px)",
          transition: "transform 0.08s ease",
        },
      }}
    >
      {/* Overlay — rest state is translucent, hover makes it denser */}
      <Box
        className="cs-overlay"
        sx={{
          position:       "absolute",
          inset:          0,
          zIndex:         2,
          display:        "flex",
          flexDirection:  "column",
          alignItems:     "center",
          justifyContent: "center",
          gap:            "10px",
          backdropFilter: "blur(1.5px)",
          background:     isDark
            ? "rgba(8,13,13,0.52)"
            : "rgba(240,245,245,0.52)",
          transition:     "backdrop-filter 0.25s ease, background 0.25s ease",
        }}
      >
        {/* Lock icon */}
        <Box
          className="cs-lock"
          sx={{
            opacity:    0.28,
            transition: "opacity 0.25s ease, transform 0.25s ease",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke={t.text2} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </Box>

        {/* Coming Soon pill */}
        <Box
          className="cs-pill"
          sx={{
            display:      "inline-flex",
            alignItems:   "center",
            gap:          "6px",
            padding:      "6px 14px",
            borderRadius: "2px",
            background:   isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
            border:       `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.09)"}`,
            transition:   "border-color 0.25s ease, background 0.25s ease",
          }}
        >
          {/* Pulsing dot */}
          <Box
            sx={{
              width:        "5px",
              height:       "5px",
              borderRadius: "50%",
              background:   accentColor,
              flexShrink:   0,
              animation:    "cs-pulse 2.2s ease-in-out infinite",
              "@keyframes cs-pulse": {
                "0%,100%": { opacity: 0.65, transform: "scale(1)" },
                "50%":     { opacity: 0.25, transform: "scale(0.7)" },
              },
            }}
          />
          <Typography
            className="cs-label"
            sx={{
              fontFamily:    "'Inter', sans-serif",
              fontSize:      "11px",
              fontWeight:    500,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color:         t.text3,
              transition:    "color 0.25s ease",
              lineHeight:    1,
            }}
          >
            Coming Soon
          </Typography>
        </Box>
      </Box>

      {/* Card content — dimmed underneath */}
      <Box sx={{ opacity: 0.28, pointerEvents: "none" }}>
        <Box
          sx={{
            display:       "inline-block",
            fontFamily:    "'Inter', 'Courier New', monospace",
            fontSize:      "9px",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            padding:       "3px 8px",
            borderRadius:  "2px",
            background:    accent2Color,
            color:         accentColor,
            mb:            "14px",
          }}
        >
          {card.badge}
        </Box>

        <Typography
          sx={{
            fontFamily:    "'Inter', sans-serif",
            fontSize:      "20px",
            fontWeight:    700,
            letterSpacing: "-0.01em",
            color:         t.text,
            mb:            "4px",
            lineHeight:    1.2,
          }}
        >
          {card.title}
        </Typography>

        <Typography
          sx={{
            fontSize:   "11.5px",
            color:      t.text3,
            fontWeight: 300,
            lineHeight: 1.65,
            mb:         "24px",
          }}
        >
          {card.desc}
        </Typography>

        <MicrosoftLoginButton onClick={undefined} />
      </Box>
    </Box>
  );
};

export default RoleCard;
